import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import './styles/Avatar.scss';

import { Stack, Typography } from '@mui/material';
import { AddAPhoto, Edit } from '@mui/icons-material';

import { classNames } from '@dw/utils';
import { getDeviceImage, IDeviceFile } from '@dw/services/filePickerService';

import { useAppSettings } from '@dw/hooks';
import { ProgressSpinner } from './ProgressSpinner';

type TIconSize = 'tiny' | 'smaller' | 'small' | 'medium' | 'large';

interface IAvatarProps {
  imageKey?: string;
  size?: TIconSize;
  canEdit?: boolean;
  disabled?: boolean;
  onNewPictureSelected?: (image: IDeviceFile) => void;
  addImageText?: string;
  shape?: 'square' | 'round';
  noBorder?: boolean;
  isLoading?: boolean;
}
export const Avatar: FunctionComponent<IAvatarProps> = (props: IAvatarProps) => {

  const { imageKey, size, canEdit, onNewPictureSelected, addImageText, shape, noBorder, disabled, isLoading } = props;
  const { appSettings } = useAppSettings();

  const [newImage, setNewImage] = useState<IDeviceFile>();

  const imageUrl = useMemo(() => {
    return !imageKey ? '' : `${appSettings.blobBaseUrl}/${imageKey}`;
  }, [imageKey, appSettings]);

  const handleImageSelect = async () => {
    if (!canEdit || disabled) {
      return;
    }

    const image = await getDeviceImage();
    if (image) {
      setNewImage(image);
      if (onNewPictureSelected) {
        onNewPictureSelected(image);
      }
    }
  }

  useEffect(() => {
    setNewImage(undefined);
  }, [canEdit]);

  return (
    <Stack
      justifyContent='center'
      alignItems='center'
      className={classNames('dw-avatar-logo', `size--${size ?? 'medium'}`, `shape--${shape ?? 'round'}`, {
        'small-font': canEdit && !imageKey && !newImage,
        'editable': canEdit && !disabled,
        'no-border': noBorder
      })}
      onClick={handleImageSelect}
    >
      {
        newImage ? (
          <img src={newImage.dataUrl} alt='' />
        ) : imageUrl ? (
          <img src={imageUrl} alt='' />
        ) : null
      }

      {
        canEdit && !disabled && !imageKey && !newImage ? (
          <Stack
            justifyContent='center'
            alignItems='center'
            rowGap={1}
            direction='column'
          >
            <AddAPhoto fontSize={size === 'large' ? 'large' : 'medium'} />
            <Typography variant={size === 'large' ? 'body1' : size === 'medium' ? 'body3' : 'caption'}>{addImageText ?? 'Add Image'}</Typography>
          </Stack>
        ) : canEdit && !disabled ? (
          <Stack className='edit-overlay' justifyContent='center' alignItems='center'>
            <Edit color='light' fontSize='small' />
          </Stack>
        ) : null
      }

      {isLoading && (
        <Stack className='loading-overlay' justifyContent='center' alignItems='center'>
          <ProgressSpinner />
        </Stack>
      )}
    </Stack>
  )
}